/**
 * Communication & Locker Room services.
 *
 * Strict rule: nothing in this file fires off email/SMS/push automatically.
 * `sendCommunication` only flips a status to 'sent' and timestamps it. Real
 * delivery integrations (SendGrid, Twilio, etc.) are wired downstream and
 * triggered by an explicit commissioner action.
 */

import type {
  Announcement,
  ActiveCommunicationChannel,
  CommunicationDelivery,
  CommunicationMessage,
  CommunicationThread,
  CommunicationThreadMessage,
  Id,
  Iso8601,
  LockerRoomMessage,
  Profile,
  Role,
  Tournament,
} from '../models';
import { getDb, updateDb, type AppDatabase } from './store';
import { newId, nowIso } from './ids';
import { getContactReadiness, type CommunicationChannel, type ContactReadiness } from './directoryService';

export const ACTIVE_COMMUNICATION_CHANNELS: ActiveCommunicationChannel[] = ['email', 'in_app'];
export const MESSAGEABLE_TOURNAMENT_STATUSES: Tournament['status'][] = [
  'scheduled',
  'in_progress',
  'paused',
  'completed',
];

export function canMessageTournament(tournament: Pick<Tournament, 'status'> | null | undefined): boolean {
  return Boolean(tournament && MESSAGEABLE_TOURNAMENT_STATUSES.includes(tournament.status));
}

export function listMessageableTournaments(db: AppDatabase = getDb()): Tournament[] {
  return [...db.tournaments]
    .filter((t) => canMessageTournament(t))
    .sort((a, b) => b.startsAt.localeCompare(a.startsAt));
}

// --------------------------------------------------------------------------
// Announcements (public — visible on Stands & Front Desk)
// --------------------------------------------------------------------------

export function listAnnouncements(tournamentId: Id): Announcement[] {
  return getDb()
    .announcements.filter((a) => a.tournamentId === tournamentId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export interface CreateAnnouncementInput {
  tournamentId: Id;
  authorProfileId: Id;
  title: string;
  body: string;
  severity?: Announcement['severity'];
  pinnedUntil?: Iso8601;
}

export function createAnnouncement(input: CreateAnnouncementInput): Announcement {
  const ts = nowIso();
  const ann: Announcement = {
    id: newId('ann'),
    kind: 'announcement',
    createdAt: ts,
    updatedAt: ts,
    tournamentId: input.tournamentId,
    authorProfileId: input.authorProfileId,
    title: input.title.trim(),
    body: input.body.trim(),
    severity: input.severity ?? 'info',
    pinnedUntil: input.pinnedUntil,
  };
  updateDb((db) => ({ ...db, announcements: [...db.announcements, ann] }));
  return ann;
}

export function deleteAnnouncement(id: Id): void {
  updateDb((db) => ({ ...db, announcements: db.announcements.filter((a) => a.id !== id) }));
}

// --------------------------------------------------------------------------
// Communications (segmented messages from commissioners)
// --------------------------------------------------------------------------

export function listCommunications(tournamentId?: Id): CommunicationMessage[] {
  return getDb()
    .communications.filter((c) => !tournamentId || c.tournamentId === tournamentId)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export interface DraftCommunicationInput {
  tournamentId?: Id;
  authorProfileId: Id;
  subject: string;
  body: string;
  audience: CommunicationMessage['audience'];
  channels: CommunicationMessage['channels'];
}

export function draftCommunication(input: DraftCommunicationInput): CommunicationMessage {
  const ts = nowIso();
  const msg: CommunicationMessage = {
    id: newId('comm'),
    kind: 'communication-message',
    createdAt: ts,
    updatedAt: ts,
    tournamentId: input.tournamentId,
    authorProfileId: input.authorProfileId,
    subject: input.subject.trim(),
    body: input.body.trim(),
    audience: input.audience,
    channels: input.channels.length > 0 ? input.channels : ['in_app'],
    status: 'draft',
  };
  updateDb((db) => ({ ...db, communications: [...db.communications, msg] }));
  return msg;
}

/**
 * Mark a draft as sent. The actual delivery transport is intentionally out of
 * scope here — UI confirms the explicit action and stamps `sentAt`.
 */
export function sendCommunication(id: Id): CommunicationMessage | undefined {
  let updated: CommunicationMessage | undefined;
  updateDb((db) => ({
    ...db,
    communications: db.communications.map((c) => {
      if (c.id !== id) return c;
      updated = { ...c, status: 'sent', sentAt: nowIso(), updatedAt: nowIso() };
      return updated;
    }),
  }));
  return updated;
}

export function deleteCommunication(id: Id): void {
  updateDb((db) => ({ ...db, communications: db.communications.filter((c) => c.id !== id) }));
}

export interface AudiencePreviewItem {
  profile: Profile;
  readiness: ContactReadiness;
  sendableChannels: ActiveCommunicationChannel[];
  skippedChannels: ActiveCommunicationChannel[];
}

export interface AudiencePreview {
  items: AudiencePreviewItem[];
  sendableRecipients: AudiencePreviewItem[];
  warningRecipients: AudiencePreviewItem[];
  blockedRecipients: AudiencePreviewItem[];
  emailRecipients: AudiencePreviewItem[];
  inAppRecipients: AudiencePreviewItem[];
  skippedDeliveries: number;
}

interface AudienceResolveOptions {
  filterUnreachable?: boolean;
  tournamentId?: Id;
}

function eventProfileIds(db: AppDatabase, tournamentId: Id): Set<Id> {
  const profileIds = new Set<Id>();
  const eventTeamIds = new Set(db.teams.filter((team) => team.tournamentId === tournamentId).map((team) => team.id));
  const eventGameIds = new Set(db.games.filter((game) => game.tournamentId === tournamentId).map((game) => game.id));

  db.roleAssignments
    .filter((assignment) => assignment.tournamentId === tournamentId)
    .forEach((assignment) => profileIds.add(assignment.profileId));

  db.teams
    .filter((team) => team.tournamentId === tournamentId && team.captainProfileId)
    .forEach((team) => {
      if (team.captainProfileId) profileIds.add(team.captainProfileId);
    });

  const eventPlayerIds = new Set(
    db.rosterEntries
      .filter((entry) => entry.tournamentId === tournamentId || eventTeamIds.has(entry.teamId))
      .map((entry) => entry.playerId),
  );
  db.players
    .filter((player) => eventPlayerIds.has(player.id))
    .forEach((player) => profileIds.add(player.profileId));

  db.registrations
    .filter((registration) =>
      registration.tournamentId === tournamentId &&
      registration.profileId &&
      registration.status !== 'declined' &&
      registration.status !== 'withdrawn',
    )
    .forEach((registration) => {
      if (registration.profileId) profileIds.add(registration.profileId);
    });

  db.gameAssignments
    .filter((assignment) => eventGameIds.has(assignment.gameId))
    .forEach((assignment) => profileIds.add(assignment.profileId));

  const eventOfficialIds = new Set(
    db.officialAssignments
      .filter((assignment) => eventGameIds.has(assignment.gameId))
      .map((assignment) => assignment.officialId),
  );
  db.officials
    .filter((official) => eventOfficialIds.has(official.id))
    .forEach((official) => profileIds.add(official.profileId));

  return profileIds;
}

function profileHasRole(db: AppDatabase, profile: Profile, role: Role, tournamentId?: Id): boolean {
  if (!tournamentId) return profile.globalRoles.includes(role);
  if (db.roleAssignments.some((assignment) => assignment.tournamentId === tournamentId && assignment.profileId === profile.id && assignment.role === role)) {
    return true;
  }
  if (role === 'captain') {
    return db.teams.some((team) => team.tournamentId === tournamentId && team.captainProfileId === profile.id);
  }
  if (role === 'player') {
    const playerIds = new Set(db.players.filter((player) => player.profileId === profile.id).map((player) => player.id));
    return db.rosterEntries.some((entry) => entry.tournamentId === tournamentId && playerIds.has(entry.playerId));
  }

  const eventGameIds = new Set(db.games.filter((game) => game.tournamentId === tournamentId).map((game) => game.id));
  if (role === 'scorekeeper') {
    return db.gameAssignments.some((assignment) =>
      eventGameIds.has(assignment.gameId) &&
      (assignment.position === 'scorekeeper' || assignment.position === 'timekeeper') &&
      assignment.profileId === profile.id,
    );
  }
  if (role === 'referee') {
    const assignedToGame = db.gameAssignments.some((assignment) =>
      eventGameIds.has(assignment.gameId) &&
      (assignment.position === 'referee_main' || assignment.position === 'referee_linesman') &&
      assignment.profileId === profile.id,
    );
    if (assignedToGame) return true;
    const official = db.officials.find((candidate) => candidate.profileId === profile.id);
    return Boolean(
      official &&
      db.officialAssignments.some((assignment) =>
        eventGameIds.has(assignment.gameId) &&
        assignment.officialId === official.id &&
        (assignment.role === 'head_referee' || assignment.role === 'referee' || assignment.role === 'linesman'),
      ),
    );
  }
  if (role === 'staff') {
    return db.gameAssignments.some((assignment) =>
      eventGameIds.has(assignment.gameId) &&
      assignment.position === 'staff' &&
      assignment.profileId === profile.id,
    );
  }

  return profile.globalRoles.includes(role);
}

/** Resolve an audience filter into the matching profiles from a supplied db. */
export function resolveAudienceFromDb(
  db: AppDatabase,
  audience: CommunicationMessage['audience'],
  channels: CommunicationChannel[] = [],
  options: AudienceResolveOptions = {},
): Profile[] {
  const allProfiles = db.profiles.filter((p) => (p.directoryStatus ?? 'active') !== 'archived');
  let pool = allProfiles;

  if (options.tournamentId) {
    const eventIds = eventProfileIds(db, options.tournamentId);
    pool = pool.filter((profile) => eventIds.has(profile.id));
  }

  if (audience.roles && audience.roles.length > 0) {
    const wanted = new Set<Role>(audience.roles);
    pool = pool.filter((profile) => Array.from(wanted).some((role) => profileHasRole(db, profile, role, options.tournamentId)));
  }

  if (audience.tags && audience.tags.length > 0) {
    const wanted = new Set(audience.tags.map((tag) => tag.trim().toLowerCase()).filter(Boolean));
    pool = pool.filter((p) =>
      (p.tags ?? []).some((tag) => wanted.has(tag.trim().toLowerCase())),
    );
  }

  if (audience.teamIds && audience.teamIds.length > 0) {
    const teamSet = new Set(audience.teamIds);
    const playerIds = new Set(
      db.rosterEntries.filter((r) => teamSet.has(r.teamId)).map((r) => r.playerId),
    );
    const profileIds = new Set(
      db.players.filter((p) => playerIds.has(p.id)).map((p) => p.profileId),
    );
    // Captains too.
    db.teams.filter((t) => teamSet.has(t.id) && t.captainProfileId).forEach((t) => {
      if (t.captainProfileId) profileIds.add(t.captainProfileId);
    });
    pool = pool.filter((p) => profileIds.has(p.id));
  }

  if (audience.divisionIds && audience.divisionIds.length > 0) {
    const divSet = new Set(audience.divisionIds);
    const teams = db.teams.filter((t) => divSet.has(t.divisionId));
    const teamIds = new Set(teams.map((t) => t.id));
    const playerIds = new Set(
      db.rosterEntries.filter((r) => teamIds.has(r.teamId)).map((r) => r.playerId),
    );
    const profileIds = new Set(
      db.players.filter((p) => playerIds.has(p.id)).map((p) => p.profileId),
    );
    teams.forEach((t) => {
      if (t.captainProfileId) profileIds.add(t.captainProfileId);
    });
    pool = pool.filter((p) => profileIds.has(p.id));
  }

  if (audience.profileIds && audience.profileIds.length > 0) {
    const wanted = new Set(audience.profileIds);
    pool = pool.filter((profile) => wanted.has(profile.id));
  }

  if (options.filterUnreachable === false) return pool;
  return pool.filter((profile) => getContactReadiness(profile, channels).status !== 'blocked');
}

/** Resolve an audience filter into the matching profiles from the active store. */
export function resolveAudience(
  audience: CommunicationMessage['audience'],
  channels: CommunicationChannel[] = [],
  options: AudienceResolveOptions = {},
): Profile[] {
  return resolveAudienceFromDb(getDb(), audience, channels, options);
}

export function getAudiencePreview(
  audience: CommunicationMessage['audience'],
  channels: ActiveCommunicationChannel[] = ['email', 'in_app'],
  db: AppDatabase = getDb(),
  options: Pick<AudienceResolveOptions, 'tournamentId'> = {},
): AudiencePreview {
  const selected = normalizeActiveChannels(channels);
  const items = resolveAudienceFromDb(db, audience, selected, { filterUnreachable: false, tournamentId: options.tournamentId }).map((profile) => {
    const readiness = getContactReadiness(profile, selected);
    const sendableChannels = selected.filter((channel) =>
      readiness.availableChannels.includes(channel),
    );
    const skippedChannels = selected.filter((channel) => !sendableChannels.includes(channel));
    return { profile, readiness, sendableChannels, skippedChannels };
  });

  return {
    items,
    sendableRecipients: items.filter((item) => item.sendableChannels.length > 0),
    warningRecipients: items.filter((item) => item.readiness.status === 'warning'),
    blockedRecipients: items.filter((item) => item.sendableChannels.length === 0),
    emailRecipients: items.filter((item) => item.sendableChannels.includes('email')),
    inAppRecipients: items.filter((item) => item.sendableChannels.includes('in_app')),
    skippedDeliveries: items.reduce((sum, item) => sum + item.skippedChannels.length, 0),
  };
}

export interface LeagueCommunicationSendInput {
  tournamentId?: Id;
  authorProfileId: Id;
  subject: string;
  body: string;
  audience: CommunicationMessage['audience'];
  channels: ActiveCommunicationChannel[];
  postAsAnnouncement?: boolean;
}

export interface DeliveryResultPatch {
  deliveryId: Id;
  status: 'sent' | 'failed';
  providerMessageId?: string;
  error?: string;
}

export interface CommunicationSendSummary {
  matchedRecipients: number;
  contactableRecipients: number;
  emailQueued: number;
  inAppSent: number;
  skippedDeliveries: number;
  failedDeliveries: number;
}

export interface PreparedCommunicationSend {
  db: AppDatabase;
  communication: CommunicationMessage;
  thread: CommunicationThread;
  message: CommunicationThreadMessage;
  deliveries: CommunicationDelivery[];
  emailDeliveries: CommunicationDelivery[];
  preview: AudiencePreview;
  summary: CommunicationSendSummary;
}

export function prepareCommunicationSend(
  db: AppDatabase,
  input: LeagueCommunicationSendInput,
  options: { now?: string; idFactory?: (prefix: string) => string } = {},
): PreparedCommunicationSend {
  const now = options.now ?? nowIso();
  const makeId = options.idFactory ?? newId;
  const tournamentId = input.tournamentId?.trim() || undefined;
  const tournament = tournamentId ? db.tournaments.find((t) => t.id === tournamentId) : undefined;
  if (tournamentId && !tournament) throw new Error('Selected event was not found.');
  if (tournament && !canMessageTournament(tournament)) {
    throw new Error('Communication Hub is available after an event is published.');
  }
  if (!tournamentId) {
    if (input.audience.teamIds?.length || input.audience.divisionIds?.length) {
      throw new Error('Team and division targeting requires an event.');
    }
    if (input.postAsAnnouncement) {
      throw new Error('Announcements require a published event.');
    }
  }

  const subject = input.subject.trim();
  const body = input.body.trim();
  if (!subject) throw new Error('Subject is required.');
  if (!body) throw new Error('Message body is required.');

  const channels = normalizeActiveChannels(input.channels);
  const preview = getAudiencePreview(input.audience, channels, db, { tournamentId });
  if (preview.sendableRecipients.length === 0) {
    throw new Error('No contactable recipients match this audience.');
  }

  const communicationId = makeId('comm');
  const threadId = makeId('thread');
  const messageId = makeId('threadmsg');
  const recipientProfileIds = preview.sendableRecipients.map((item) => item.profile.id);
  const participantProfileIds = Array.from(new Set([input.authorProfileId, ...recipientProfileIds]));

  const deliveries: CommunicationDelivery[] = [];
  for (const item of preview.items) {
    for (const channel of channels) {
      const deliveryId = makeId('delivery');
      const isAvailable = item.sendableChannels.includes(channel);
      deliveries.push({
        id: deliveryId,
        kind: 'communication-delivery',
        createdAt: now,
        updatedAt: now,
        threadId,
        messageId,
        communicationId,
        recipientProfileId: item.profile.id,
        recipientEmail: item.profile.email,
        channel,
        provider: channel === 'email' ? 'resend' : 'in_app',
        status: isAvailable ? (channel === 'email' ? 'queued' : 'sent') : 'skipped',
        skippedReason: isAvailable ? undefined : channelSkipReason(item.readiness, channel),
        sentAt: isAvailable && channel === 'in_app' ? now : undefined,
      });
    }
  }

  const communication: CommunicationMessage = {
    id: communicationId,
    kind: 'communication-message',
    createdAt: now,
    updatedAt: now,
    tournamentId,
    authorProfileId: input.authorProfileId,
    audience: input.audience,
    subject,
    body,
    sentAt: now,
    status: 'sent',
    channels,
    threadId,
  };

  const thread: CommunicationThread = {
    id: threadId,
    kind: 'communication-thread',
    createdAt: now,
    updatedAt: now,
    tournamentId,
    subject,
    scope: tournamentId ? 'event' : 'league',
    status: 'open',
    createdByProfileId: input.authorProfileId,
    participantProfileIds,
    recipientProfileIds,
    audience: input.audience,
    sourceCommunicationId: communicationId,
    lastMessageAt: now,
  };

  const message: CommunicationThreadMessage = {
    id: messageId,
    kind: 'communication-thread-message',
    createdAt: now,
    updatedAt: now,
    threadId,
    authorProfileId: input.authorProfileId,
    direction: 'outbound',
    source: 'league_hq',
    subject,
    body,
    channels,
    deliveryIds: deliveries.map((delivery) => delivery.id),
  };

  const announcements = input.postAsAnnouncement
    ? [
        {
          id: makeId('ann'),
          kind: 'announcement' as const,
          createdAt: now,
          updatedAt: now,
          tournamentId: tournamentId!,
          authorProfileId: input.authorProfileId,
          title: subject,
          body,
          severity: 'info' as const,
        },
      ]
    : [];

  const nextDb: AppDatabase = {
    ...db,
    communications: [...db.communications, communication],
    communicationThreads: [...db.communicationThreads, thread],
    communicationThreadMessages: [...db.communicationThreadMessages, message],
    communicationDeliveries: [...db.communicationDeliveries, ...deliveries],
    announcements: [...db.announcements, ...announcements],
  };

  const emailDeliveries = deliveries.filter(
    (delivery) => delivery.channel === 'email' && delivery.status === 'queued',
  );

  return {
    db: nextDb,
    communication,
    thread,
    message,
    deliveries,
    emailDeliveries,
    preview,
    summary: {
      matchedRecipients: preview.items.length,
      contactableRecipients: preview.sendableRecipients.length,
      emailQueued: emailDeliveries.length,
      inAppSent: deliveries.filter((delivery) => delivery.channel === 'in_app' && delivery.status === 'sent').length,
      skippedDeliveries: deliveries.filter((delivery) => delivery.status === 'skipped').length,
      failedDeliveries: 0,
    },
  };
}

export function applyDeliveryResultsToDb(
  db: AppDatabase,
  patches: DeliveryResultPatch[],
  now = nowIso(),
): AppDatabase {
  if (patches.length === 0) return db;
  const byDeliveryId = new Map(patches.map((patch) => [patch.deliveryId, patch]));
  return {
    ...db,
    communicationDeliveries: db.communicationDeliveries.map((delivery) => {
      const patch = byDeliveryId.get(delivery.id);
      if (!patch) return delivery;
      return {
        ...delivery,
        status: patch.status,
        providerMessageId: patch.providerMessageId,
        error: patch.error,
        sentAt: patch.status === 'sent' ? now : delivery.sentAt,
        updatedAt: now,
      };
    }),
  };
}

export function createLocalCommunicationSend(input: LeagueCommunicationSendInput): PreparedCommunicationSend {
  let prepared: PreparedCommunicationSend | undefined;
  updateDb((db) => {
    prepared = prepareCommunicationSend(db, input);
    return prepared.db;
  });
  if (!prepared) throw new Error('Could not prepare communication.');
  return prepared;
}

export function listCommunicationThreads(options: { profileId?: Id; tournamentId?: Id } = {}): CommunicationThread[] {
  return [...getDb().communicationThreads]
    .filter((thread) => !options.profileId || thread.participantProfileIds.includes(options.profileId))
    .filter((thread) => !options.tournamentId || thread.tournamentId === options.tournamentId)
    .filter((thread) => thread.status !== 'archived')
    .sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt));
}

export function listThreadMessages(threadId: Id): CommunicationThreadMessage[] {
  return getDb()
    .communicationThreadMessages.filter((message) => message.threadId === threadId)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export function listThreadDeliveries(threadId: Id): CommunicationDelivery[] {
  return getDb().communicationDeliveries.filter((delivery) => delivery.threadId === threadId);
}

export function postThreadReply(input: {
  threadId: Id;
  authorProfileId: Id;
  body: string;
  direction?: 'outbound' | 'inbound';
}): CommunicationThreadMessage {
  const body = input.body.trim();
  if (!body) throw new Error('Reply body is required.');
  const now = nowIso();
  let created: CommunicationThreadMessage | undefined;
  updateDb((db) => {
    const thread = db.communicationThreads.find((t) => t.id === input.threadId);
    if (!thread) throw new Error('Thread was not found.');
    const messageId = newId('threadmsg');
    const deliveryIds: Id[] = [];
    const deliveries = thread.participantProfileIds
      .filter((profileId) => profileId !== input.authorProfileId)
      .map((profileId) => {
        const id = newId('delivery');
        deliveryIds.push(id);
        return {
          id,
          kind: 'communication-delivery' as const,
          createdAt: now,
          updatedAt: now,
          threadId: thread.id,
          messageId,
          recipientProfileId: profileId,
          channel: 'in_app' as const,
          provider: 'in_app' as const,
          status: 'sent' as const,
          sentAt: now,
        };
      });
    created = {
      id: messageId,
      kind: 'communication-thread-message',
      createdAt: now,
      updatedAt: now,
      threadId: thread.id,
      authorProfileId: input.authorProfileId,
      direction: input.direction ?? 'inbound',
      source: input.direction === 'outbound' ? 'league_hq' : 'recipient_app',
      body,
      channels: ['in_app'],
      deliveryIds,
    };
    return {
      ...db,
      communicationThreads: db.communicationThreads.map((t) =>
        t.id === thread.id ? { ...t, updatedAt: now, lastMessageAt: now } : t,
      ),
      communicationThreadMessages: [...db.communicationThreadMessages, created!],
      communicationDeliveries: [...db.communicationDeliveries, ...deliveries],
    };
  });
  if (!created) throw new Error('Could not post reply.');
  return created;
}

function normalizeActiveChannels(channels: ActiveCommunicationChannel[]): ActiveCommunicationChannel[] {
  const selected = Array.from(new Set(channels.filter((channel) => ACTIVE_COMMUNICATION_CHANNELS.includes(channel))));
  return selected.length > 0 ? selected : ['in_app'];
}

function channelSkipReason(readiness: ContactReadiness, channel: ActiveCommunicationChannel): string {
  const key = channel === 'email' ? 'email' : 'in-app';
  return readiness.issues.find((issue) => issue.toLowerCase().includes(key)) ?? readiness.issues[0] ?? `${channel} is unavailable.`;
}

// --------------------------------------------------------------------------
// Locker Room (team-private chat)
// --------------------------------------------------------------------------

export function listLockerRoomMessages(tournamentId: Id, teamId: Id): LockerRoomMessage[] {
  return getDb()
    .lockerRoomMessages.filter((m) => m.tournamentId === tournamentId && m.teamId === teamId)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export interface PostLockerMessageInput {
  tournamentId: Id;
  teamId: Id;
  authorProfileId: Id;
  body: string;
}

export function postLockerMessage(input: PostLockerMessageInput): LockerRoomMessage {
  const ts = nowIso();
  const msg: LockerRoomMessage = {
    id: newId('lr'),
    kind: 'locker-room-message',
    createdAt: ts,
    updatedAt: ts,
    tournamentId: input.tournamentId,
    teamId: input.teamId,
    authorProfileId: input.authorProfileId,
    body: input.body.trim(),
    reactions: {},
  };
  updateDb((db) => ({ ...db, lockerRoomMessages: [...db.lockerRoomMessages, msg] }));
  return msg;
}

export function deleteLockerMessage(id: Id): void {
  updateDb((db) => ({
    ...db,
    lockerRoomMessages: db.lockerRoomMessages.filter((m) => m.id !== id),
  }));
}

// --------------------------------------------------------------------------
// Communication templates (subject + body skeletons)
// --------------------------------------------------------------------------

export interface MessageTemplate {
  id: string;
  label: string;
  subject: string;
  body: string;
}

export const TEMPLATES: MessageTemplate[] = [
  {
    id: 'welcome',
    label: 'Welcome / intro',
    subject: 'Welcome to {{tournament}}',
    body: 'Hey team — looking forward to seeing you at {{tournament}}. Stay tuned for the schedule and any logistics updates.',
  },
  {
    id: 'schedule',
    label: 'Schedule released',
    subject: '{{tournament}} schedule is live',
    body: 'The schedule is published. Check the Stands or your Player Dashboard for your games.',
  },
  {
    id: 'crew',
    label: 'Scorekeeper / ref assignment',
    subject: 'Your assignment for {{tournament}}',
    body: 'You\'ve been booked on a few games. Open the Scorekeeper Command Center to see your queue and confirm.',
  },
  {
    id: 'delay',
    label: 'Delay / update',
    subject: 'Heads up — small change to today\'s schedule',
    body: 'A short note: there\'s been an update. Check the Front Desk for the latest.',
  },
  {
    id: 'captain',
    label: 'Captain reminder',
    subject: 'Captains — confirm your roster',
    body: 'Captains, please confirm your roster and arrival time in the Captain Command Center before puck drop.',
  },
];
